import Image from "next/image";

import styles from "@/styles/style.module.scss";
import ImgFeaturesPayment from "@/assets/illustrations/features-payment.svg";
import ImgFeaturesInvoice from "@/assets/illustrations/features-invoice.svg";
import BgFeatures from "@/assets/illustrations/bg-features.png";
import MotionSection from "@/components/motion/section";
import EasyImplementationIcon from "@/assets/icons/features-easy-implementation.svg";
import EasyUsabilityIcon from "@/assets/icons/features-easy-usability.svg";
import MakingWorkFunIcon from "@/assets/icons/features-making-work-fun.svg";

interface CardInfo {
  title: string;
  short_description: string;
  icon: string;
}

export default function Features() {
  const cardInfos: CardInfo[] = [
    {
      title: "Easy Implementation",
      short_description: "Get up and running with zanbio in weeks not months",
      icon: EasyImplementationIcon,
    },
    {
      title: "Designed for people",
      short_description:
        "User friendly interface requiring little to no training",
      icon: EasyUsabilityIcon,
    },
    {
      title: "Simplify your work",
      short_description:
        "Let zanbio do the heavy lifting and replace chronic spreadsheet pain ",
      icon: MakingWorkFunIcon,
    },
  ];

  const CardInfo = ({ info }: { info: CardInfo }) => {
    return (
      <div className={styles.features__infos_item}>
        <Image
          alt="features-icon"
          className="size-[68px] mb-6 -ml-1"
          height={100}
          src={info.icon}
          width={100}
        />
        <h4>{info.title}</h4>
        <h5>{info.short_description}</h5>
      </div>
    );
  };

  return (
    <div className={styles.features}>
      <div
        className={styles.features__background}
        style={{
          backgroundImage: `url(${BgFeatures.src})`,
        }}
      />
      <div className={styles.features__container}>
        <div className={styles.features__head}>
          <h2 className={styles.text_heading_1}>Features</h2>
          <h3 className={`${styles.text_super} ${styles.features__title}`}>
            <span className={styles.text_gradient_primary}>Raise</span> your
            finance team&apos;s <br /> quality of life
          </h3>
          <h4
            className={`${styles.text_subtitle} ${styles.features__subtitle}`}
          >
            zanbio helps finance teams to increase operational efficiency by
            simplifying workflows and automating repetitive tasks <br /> <br />
            Employees can focus on key tasks, raises total productivity and
            improve customer relationships
          </h4>
        </div>

        <div className={styles.features__infos}>
          {cardInfos.map((info, index) => (
            <CardInfo key={`${info.title} with key ${index}`} info={info} />
          ))}
        </div>

        <MotionSection directions="left">
          <div className={styles.features__content}>
            <div className={styles.features__content_container}>
              <h4
                className={`${styles.text_heading_1}  ${styles.features__content_head}`}
              >
                Products
              </h4>
              <h5
                className={`${styles.text_super} ${styles.features__content_title}`}
              >
                Accounts Receivable Automation
              </h5>
              <h6
                className={`${styles.text_subtitle} ${styles.features__content_subtitle}`}
              >
                zanbio AR goes beyond emailing invoices and offering digital
                payment options. It speeds up payments (reduced DSO), cut the
                cost, time and effort of chasing payments while improving
                cashflow and your customer experience.
                <br />
                <br />
                zanbio uses smart workflow tools, powered by data analytics and
                machine learning, to help you get paid faster. Streamline your
                processes and optimize your working capital with our world-class
                invoice-to-cash solution.
              </h6>
            </div>
            <div className={styles.features__content_img}>
              <Image
                priority
                alt="features-payment"
                className="size-full"
                height={100}
                src={ImgFeaturesPayment}
                width={100}
              />
            </div>
          </div>
        </MotionSection>
        <MotionSection directions="right">
          <div
            className={`${styles.features__content} ${styles.features__content_swap}`}
          >
            <div className={styles.features__content_container}>
              <h4
                className={`${styles.text_heading_1}  ${styles.features__content_head}`}
              >
                Products
              </h4>
              <h5
                className={`${styles.text_super} ${styles.features__content_title}`}
              >
                Accounts Payable Automation
              </h5>
              <h6
                className={`${styles.text_subtitle} ${styles.features__content_subtitle}`}
              >
                While sales and revenue get most of the focus, managing expenses
                and vendor relationships is just as crucial for profitability
                and sustainability.
                <br />
                <br />
                zanbio AP simplifies your accounts payable process, boosting
                efficiency and accuracy while protecting your business from
                fraud, overpayments, and duplicate invoices, leading to better
                cash management.
              </h6>
            </div>
            <div className={styles.features__content_img}>
              <Image
                priority
                alt="features-payment"
                className="size-full"
                height={100}
                src={ImgFeaturesInvoice}
                width={100}
              />
            </div>
          </div>
        </MotionSection>
      </div>
    </div>
  );
}
